import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, Search, ShieldCheck, KeyRound, TrendingUp, Phone } from "lucide-react";
import { API, IMG, BRAND, BUDGET_OPTIONS } from "../lib/site";
import { MaskedLine, Reveal, SectionHeading, Tilt } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { StatsCounter } from "../components/StatsCounter";
import { ListingCard } from "../components/ListingCard";
import { LeadForm } from "../components/LeadForm";
import { useLead } from "../components/LeadModal";

const selectCls =
  "h-12 w-full border border-navy-900/15 bg-white px-4 text-sm text-navy-900 outline-none transition-[border-color,box-shadow] duration-300 focus:border-gold-500 focus:ring-2 focus:ring-gold-500/25 appearance-none cursor-pointer";

const Hero = () => {
  const mx = useMotionValue(0);
  const myv = useMotionValue(0);
  const smx = useSpring(mx, { stiffness: 45, damping: 18 });
  const smy = useSpring(myv, { stiffness: 45, damping: 18 });
  const imgX = useTransform(smx, [-1, 1], [-14, 14]);
  const imgY = useTransform(smy, [-1, 1], [-9, 9]);
  const navigate = useNavigate();
  const { openLead } = useLead();
  const [search, setSearch] = useState({ type: "", budget: "", location: "" });

  const runSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (search.budget) params.set("budget", search.budget);
    if (search.location) params.set("location", search.location);
    const q = params.toString();
    if (search.type === "Pre-Launch Investment") navigate("/projects");
    else if (search.type === "Rental Property") navigate(`/rentals${q ? `?${q}` : ""}`);
    else navigate(`/resale${q ? `?${q}` : ""}`);
  };

  return (
    <section
      className="relative overflow-hidden bg-navy-950"
      data-testid="home-hero"
      onMouseMove={(e) => {
        mx.set((e.clientX / window.innerWidth - 0.5) * 2);
        myv.set((e.clientY / window.innerHeight - 0.5) * 2);
      }}
    >
      <motion.img
        src={IMG.hero}
        alt=""
        aria-hidden="true"
        style={{ x: imgX, y: imgY }}
        className="absolute inset-0 h-full w-full scale-[1.04] object-cover object-center lg:hidden"
      />
      <img
        src={IMG.hero}
        alt="Luxury villa at dusk"
        className="hidden h-auto w-full lg:block"
      />
      <div className="relative flex min-h-[100svh] items-center px-6 pb-16 pt-28 lg:absolute lg:inset-0 lg:min-h-0 lg:px-0 lg:pb-0 lg:pt-16">
        <div className="mx-auto w-full max-w-7xl lg:px-10">
        <MaskedLine>
          <p className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.32em] text-gold-400 sm:text-sm [text-shadow:0_1px_8px_rgb(8,16,39,0.9),0_2px_18px_rgb(8,16,39,0.7)]">
            <span className="inline-block h-px w-10 bg-gold-500" aria-hidden="true" />
            Estate Paradise &middot; Real Estate Brokers
          </p>
        </MaskedLine>
        <h1 className="mt-7 max-w-4xl font-serif text-[2.2rem] font-light leading-[1.06] tracking-tight text-white [text-shadow:0_2px_10px_rgb(8,16,39,0.85),0_4px_36px_rgb(8,16,39,0.7)] sm:text-5xl lg:text-6xl">
          <MaskedLine delay={0.15}>Find Your Dream Home</MaskedLine>
          <MaskedLine delay={0.3}>
            &amp; <span className="italic text-gold-400">Smart Investments</span>
          </MaskedLine>
          <MaskedLine delay={0.45}>in NCR.</MaskedLine>
        </h1>
        <MaskedLine delay={0.6}>
          <p className="mt-7 max-w-xl text-sm font-medium leading-relaxed text-white [text-shadow:0_1px_8px_rgb(8,16,39,0.9),0_2px_20px_rgb(8,16,39,0.75)] sm:text-base">
            Pre-launch flagships, handpicked resale flats and premium rentals across Ghaziabad &amp; Noida
            Extension — guided by a 4.9-star rated brokerage you can trust.
          </p>
        </MaskedLine>

        <motion.div
          initial={{ opacity: 0, y: 26 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/projects"
            data-testid="hero-explore-btn"
            className="group flex items-center gap-2 bg-gold-500 px-7 py-4 text-[13px] font-bold uppercase tracking-[0.14em] text-navy-900 shadow-[0_14px_34px_rgb(197,160,89,0.4)] transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-1 hover:bg-gold-400"
          >
            Explore Pre-Launch
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
          </Link>
          <button
            data-testid="hero-visit-btn"
            onClick={() => openLead({ title: "Schedule Your Site Visit", interest: "Schedule Site Visit", source: "Hero CTA" })}
            className="border border-white/40 bg-navy-950/25 px-7 py-4 text-[13px] font-bold uppercase tracking-[0.14em] text-white backdrop-blur-sm transition-[border-color,background-color,transform] duration-300 hover:-translate-y-1 hover:border-gold-400 hover:text-gold-400"
          >
            Schedule Site Visit
          </button>
        </motion.div>

        <motion.form
          onSubmit={runSearch}
          initial={{ opacity: 0, y: 34 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.85, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 max-w-4xl border border-white/25 bg-white/90 p-4 shadow-[0_30px_70px_rgb(8,16,39,0.35)] backdrop-blur-xl sm:p-5 lg:mt-12"
          data-testid="hero-search-bar"
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_1fr_auto]">
            <select data-testid="search-type-select" value={search.type} onChange={(e) => setSearch((s) => ({ ...s, type: e.target.value }))} className={selectCls}>
              <option value="">Property Type</option>
              <option>Pre-Launch Investment</option>
              <option>Buy / Resale Flat</option>
              <option>Rental Property</option>
            </select>
            <select data-testid="search-budget-select" value={search.budget} onChange={(e) => setSearch((s) => ({ ...s, budget: e.target.value }))} className={selectCls}>
              <option value="">Budget</option>
              {BUDGET_OPTIONS.map((b) => (
                <option key={b}>{b}</option>
              ))}
            </select>
            <select data-testid="search-location-select" value={search.location} onChange={(e) => setSearch((s) => ({ ...s, location: e.target.value }))} className={selectCls}>
              <option value="">Location</option>
              <option>Siddharth Vihar</option>
              <option>Wave City</option>
              <option>Aditya World City</option>
              <option>Noida Extension</option>
              <option>Gaur City</option>
              <option>Indirapuram</option>
              <option>Vaishali</option>
            </select>
            <button
              type="submit"
              data-testid="search-submit-btn"
              className="flex h-12 items-center justify-center gap-2 bg-navy-900 px-7 text-[13px] font-bold uppercase tracking-[0.14em] text-white transition-colors duration-300 hover:bg-navy-700"
            >
              <Search className="h-4 w-4" />
              Search
            </button>
          </div>
        </motion.form>
        </div>
      </div>
    </section>
  );
};

const Specialties = () => {
  const items = [
    {
      title: "Pre-Launch Investment Projects",
      text: "First-mover pricing on NCR's most anticipated towers — floor-rise advantages and launch-window payment plans.",
      image: IMG.arch,
      to: "/projects",
      icon: TrendingUp,
    },
    {
      title: "Residential Resale Flats",
      text: "Verified, ready-to-move homes with clean titles, fair valuations and end-to-end documentation support.",
      image: IMG.living,
      to: "/resale",
      icon: KeyRound,
    },
    {
      title: "Premium Rental Properties",
      text: "Furnished and semi-furnished homes with transparent terms, quick move-ins and managed handovers.",
      image: IMG.rental2,
      to: "/rentals",
      icon: ShieldCheck,
    },
  ];
  return (
    <section className="bg-cream py-24 sm:py-32" data-testid="specialties-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
          <SectionHeading
            overline="Our Specialties"
            title={<>Three ways we move you <span className="italic text-gold-600">closer to home</span></>}
          />
          <Reveal delay={0.2}>
            <p className="max-w-sm text-sm leading-relaxed text-gray-500 lg:pb-2 lg:text-right">
              Every listing is physically verified by our team before it reaches you — no inflated photos, no hidden charges.
            </p>
          </Reveal>
        </div>
        <div className="mt-16 grid grid-cols-1 gap-8 md:grid-cols-3" style={{ perspective: "1400px" }}>
          {items.map((item, i) => (
            <Reveal key={item.title} delay={i * 0.12} depth className={i === 1 ? "h-full lg:translate-y-12" : "h-full"}>
              <Tilt className="h-full" max={5}>
                <Link
                  to={item.to}
                  data-testid={`specialty-card-${i}`}
                  className="group relative block h-full overflow-hidden border border-navy-900/10 bg-white shadow-[0_8px_30px_rgb(13,27,62,0.05)] transition-[transform,box-shadow] duration-500 hover:shadow-[0_32px_64px_rgb(13,27,62,0.16)]"
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img src={item.image} alt={item.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.07]" />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy-950/50 to-transparent" />
                    <item.icon className="absolute bottom-5 left-5 h-7 w-7 text-gold-400" />
                  </div>
                  <div className="p-7">
                    <h3 className="font-serif text-lg font-medium text-navy-900 sm:text-xl">{item.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-gray-600">{item.text}</p>
                    <span className="mt-5 inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.16em] text-gold-600">
                      Discover
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                    </span>
                  </div>
                </Link>
              </Tilt>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const LeadSection = () => (
  <section className="relative overflow-hidden bg-white py-24 sm:py-32" data-testid="lead-section">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-2">
        <div>
          <SectionHeading
            overline="Zero Spam, One Call"
            title={<>Get an instant callback <span className="italic text-gold-600">&amp; the full price list</span></>}
            description="Tell us what you're looking for. A senior advisor — not a call centre — rings you back within 30 minutes with verified options and honest pricing."
          />
          <Reveal delay={0.2}>
            <ul className="mt-8 space-y-4">
              {["Verified inventory only — every property physically inspected", "Best-negotiated launch & resale pricing, in writing", "Free site-visit cab within Ghaziabad & Noida Extension"].map((t, i) => (
                <li key={i} className="flex items-start gap-3 text-[15px] text-gray-600">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-gold-500" aria-hidden="true" />
                  {t}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.28}>
            <a href={`tel:+${BRAND.phoneRaw}`} className="mt-9 inline-flex items-center gap-3 border-b-2 border-gold-500 pb-1 font-serif text-lg text-navy-900 transition-colors duration-300 hover:text-gold-600" data-testid="lead-call-link">
              <Phone className="h-5 w-5 text-gold-600" />
              {BRAND.phone}
            </a>
          </Reveal>
        </div>
        <Reveal delay={0.15} depth>
          <div className="relative">
            <div className="absolute -left-4 -top-4 h-full w-full border border-gold-500/50" aria-hidden="true" />
            <div className="relative border border-navy-900/10 bg-cream p-8 shadow-[0_30px_70px_rgb(13,27,62,0.1)] sm:p-10">
              <LeadForm tid="home-lead-form" source="Home Lead Form" submitLabel="Get Instant Callback" />
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const ClosingBand = () => {
  const { openLead } = useLead();
  return (
    <section className="relative overflow-hidden bg-navy-900 py-24 sm:py-28" data-testid="closing-band">
      <img src={IMG.skyline} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
      <div className="absolute inset-0 bg-gradient-to-r from-navy-950/90 to-navy-900/70" />
      <div className="relative mx-auto flex max-w-7xl flex-col items-start gap-8 px-6 lg:flex-row lg:items-center lg:justify-between lg:px-10">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-gold-400">Begin Today</p>
          <h2 className="mt-4 max-w-xl font-serif text-2xl font-light leading-tight text-white sm:text-3xl lg:text-4xl">
            Your keys are <span className="italic text-gold-400">one conversation</span> away.
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <button
            data-testid="closing-cta-btn"
            onClick={() => openLead({ title: "Schedule Your Site Visit", interest: "Schedule Site Visit", source: "Closing Band" })}
            className="group flex items-center gap-2 bg-gold-500 px-8 py-4 text-[13px] font-bold uppercase tracking-[0.14em] text-navy-900 transition-[transform,background-color] duration-300 hover:-translate-y-1 hover:bg-gold-400"
          >
            Book a Free Site Visit
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
          </button>
        </Reveal>
      </div>
    </section>
  );
};

const Flagships = () => {
  const [listings, setListings] = useState([]);
  useEffect(() => {
    axios.get(`${API}/listings`, { params: { category: "prelaunch" } }).then((r) => setListings(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <section className="bg-white pb-24 sm:pb-32" data-testid="flagships-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
          <SectionHeading
            overline="Exclusive Opportunities"
            title={<>Pre-launch <span className="italic text-gold-600">flagships</span> open now</>}
            description="Launch-window pricing on three signature addresses — allocations are limited and floor-rise begins soon."
          />
          <Reveal delay={0.2}>
            <Link to="/projects" className="group inline-flex items-center gap-2 border-b-2 border-navy-900 pb-1 text-[12px] font-bold uppercase tracking-[0.18em] text-navy-900 transition-colors duration-300 hover:border-gold-500 hover:text-gold-600" data-testid="view-all-projects-link">
              View All Projects
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
            </Link>
          </Reveal>
        </div>
        <div className="mt-16 grid grid-cols-1 gap-8 lg:grid-cols-5" style={{ perspective: "1600px" }}>
          {listings.map((l, i) => (
            <div key={l.id} className={i === 0 ? "lg:col-span-3" : "lg:col-span-2"}>
              <ListingCard listing={l} index={i} featured={i === 0} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <Marquee />
      <StatsCounter />
      <Flagships />
      <Specialties />
      <LeadSection />
      <ClosingBand />
    </div>
  );
}
